import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function Home() {
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const toggleQuestion = (index: number) => {
    setActiveQuestion(activeQuestion === index ? null : index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://forms.readdy.ai/submit/cm5qkdmqq0003m6o6xzqq7aqz', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          assunto: formData.assunto,
          mensagem: formData.mensagem
        })
      });

      if (response.ok) {
        setSubmitMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          assunto: '',
          mensagem: ''
        });
      } else {
        setSubmitMessage('Erro ao enviar mensagem. Tente novamente.');
      }
    } catch (error) {
      setSubmitMessage('Erro ao enviar mensagem. Tente novamente.');
    }

    setIsSubmitting(false);
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/5517997346470', '_blank');
  };

  const faqItems = [
    {
      question: 'Em quais áreas atua como perito?',
      answer: 'Atuo principalmente nas áreas Trabalhista e Cível, com especialização em insalubridade, periculosidade, ergonomia, consumo de energia, máquinas e equipamentos, veículos automotores e sistemas de climatização.'
    },
    {
      question: 'Atende como Perito Judicial e Assistente Técnico?',
      answer: 'Sim, atendo de forma ética em ambas as funções, sempre respeitando os nomes e contratos específicos de cada modalidade.'
    },
    {
      question: 'Como formalizar a contratação?',
      answer: 'Envie o número do processo e documentos relevantes para análise inicial. Após avaliação, fornecerei proposta detalhada com cronograma e valores.'
    },
    {
      question: 'Qual o prazo para elaboração de laudos?',
      answer: 'Os prazos variam conforme a complexidade do caso, mas mantenho rigoroso cumprimento dos prazos judiciais e acordo com as partes.'
    },
    {
      question: 'Atende em quais regiões?',
      answer: 'Atendo principalmente no TJ/SP e TRT-15, mas posso atuar em outras regiões conforme demanda.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <SchemaOrg type="Person" />
      <SchemaOrg type="ProfessionalService" />
      <SchemaOrg type="Organization" />
      
      <Header />
      
      {/* Hero Section */}
      <section 
        id="inicio"
        className="relative bg-gradient-to-br from-slate-50 to-slate-100 py-20 lg:py-32"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/eea6bba7520d635e571b9db58e403fca.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-white/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="w-full">
              <div className="mb-6">
                <span className="inline-block bg-green-100 text-green-800 px-6 py-3 rounded-full text-lg font-semibold shadow-md border border-green-200">
                  Perito Judicial • Assistente Técnico
                </span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Perícias com clareza e rigor pericial
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                13 anos de experiência em perícias de engenharia. Atuação no TJ/SP e TRT‑15, 
                com metodologia focada em evidências e cumprimento rigoroso de prazos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={openWhatsApp} size="lg">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-whatsapp-line"></i>
                  </div>
                  Falar no WhatsApp
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Solicitar Orçamento
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/73dfcd03d9821e3620f8279bf4d61d7d/06f260c3ffab18ce4c6854acfb4ad000.png"
                  alt="Eng. João Paulo Scardelato Severino"
                  className="w-80 h-96 object-cover object-top rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credenciais */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-slate-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-check-line text-slate-700 text-2xl"></i>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-600">CREA Ativo</p>
            </div>
            <div className="text-center">
              <div className="bg-slate-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-slate-700 text-2xl"></i>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-600">Cadastro Nacional de Peritos</p>
            </div>
            <div className="text-center">
              <div className="bg-slate-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-award-line text-slate-700 text-2xl"></i>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-600">ART quando aplicável</p>
            </div>
            <div className="text-center">
              <div className="bg-slate-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-file-text-line text-slate-700 text-2xl"></i>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-600">Emissão de Nota Fiscal</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre */}
      <section id="sobre" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Sobre o Profissional
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Com 13 anos de atuação em perícias de engenharia (judiciais e extrajudiciais), 
                desenvolvi metodologia focada em evidências — registros fotográficos, orientação 
                e bibliografia normativa.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Atendo ao TJ/SP e TRT‑15, inclusive em Justiça Gratuita. Pareceres objetivos, 
                linguagem acessível e cumprimento rigoroso de prazos.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">Formação Acadêmica</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-slate-700">Engenharia Elétrica — UNIFEB (2008)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-slate-700">Engenharia Mecânica — UniAnhanguera (2012)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-slate-700">Engenharia de Segurança do Trabalho — FACUMINAS (2025)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-slate-700">Pós‑Graduação — Ergonomia e Saúde Ocupacional</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-slate-700">Pós‑Graduação — Perícia Judicial</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://static.readdy.ai/image/73dfcd03d9821e3620f8279bf4d61d7d/8f6c3d2f2580a8e1ced3b7fe665c2201.png"
                alt="Ambiente de trabalho profissional"
                className="w-full max-w-md h-96 object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section id="servicos" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Áreas de Atuação & Serviços
            </h2>
            <p className="text-xl text-slate-600">
              Perícias Judiciais e Assistência Técnica especializadas
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-green-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-shield-cross-line text-green-600 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Insalubridade & Periculosidade
              </h3>
              <p className="text-slate-600">
                Perícias técnicas conforme NR‑15 e NR‑16, com análise detalhada 
                dos agentes de risco no ambiente de trabalho.
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-blue-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-user-heart-line text-blue-600 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Ergonomia & Saúde Ocupacional
              </h3>
              <p className="text-slate-600">
                Avaliação ergonômica completa dos postos de trabalho e 
                análise de condições de saúde ocupacional.
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-yellow-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-flashlight-line text-yellow-600 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Consumo de Energia
              </h3>
              <p className="text-slate-600">
                Análise técnica de consumo energético, eficiência e 
                sistemas elétricos industriais e residenciais.
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-purple-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-settings-3-line text-purple-600 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Máquinas & Equipamentos
              </h3>
              <p className="text-slate-600">
                Perícia técnica em máquinas industriais, equipamentos 
                e sistemas mecânicos diversos.
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-red-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-car-line text-red-600 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Veículos Automotores
              </h3>
              <p className="text-slate-600">
                Análise de dinâmica veicular e avaliação de danos.
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="bg-teal-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-temp-cold-line text-teal-600 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Sistemas de Climatização & PMOC
              </h3>
              <p className="text-slate-600">
                Perícias em sistemas de ar condicionado e Plano de 
                Manutenção, Operação e Controle.
              </p>
            </div>
          </div>
          
          <div className="mt-12 bg-slate-100 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center">
              Serviços Adicionais
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <div className="w-6 h-6 flex items-center justify-center mr-4">
                  <i className="ri-question-answer-line text-slate-700"></i>
                </div>
                <span className="text-slate-700">Perguntas, Impugnações e Contra-laudos</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 flex items-center justify-center mr-4">
                  <i className="ri-group-line text-slate-700"></i>
                </div>
                <span className="text-slate-700">Acompanhamento em Audiências e Vistorias</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Como Trabalho */}
      <section id="como-trabalho" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Como Trabalho
            </h2>
            <p className="text-xl text-slate-600">
              Metodologia estruturada para garantir qualidade e precisão
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-slate-900">1</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Análise Inicial</h3>
              <p className="text-slate-600">
                Escopo, questões e plano de ação detalhado
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-slate-900">2</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Agenda & Questões</h3>
              <p className="text-slate-600">
                Contato com partes e ajustes necessários
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-slate-900">3</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Vistoria Técnica</h3>
              <p className="text-slate-600">
                Inspeção in loco, medições e registros
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-slate-900">4</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Laudo/Parecer</h3>
              <p className="text-slate-600">
                Conclusões fundamentadas e anexos
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-2xl font-bold text-slate-900">5</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Esclarecimentos</h3>
              <p className="text-slate-600">
                Suporte a impugnações e audiências
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-slate-600">
              Esclareça suas principais dúvidas sobre os serviços
            </p>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-lg">
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center cursor-pointer hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <span className="font-medium text-slate-900">{item.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-${activeQuestion === index ? 'subtract' : 'add'}-line text-slate-600`}></i>
                  </div>
                </button>
                {activeQuestion === index && (
                  <div className="px-6 pb-4">
                    <p className="text-slate-600 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Precisa de uma Perícia Técnica?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Entre em contato e solicite um orçamento personalizado para seu caso
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openWhatsApp} variant="secondary" size="lg">
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-whatsapp-line"></i>
              </div>
              Falar no WhatsApp
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-white text-white hover:bg-white hover:text-slate-900"
            >
              Ver Áreas de Atuação
            </Button>
          </div>
        </div>
      </section>

      {/* Contato */}
      <section id="contato" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Entre em Contato
            </h2>
            <p className="text-xl text-slate-600">
              Solicite seu orçamento ou tire suas dúvidas
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-6">
                Informações de Contato
              </h3>
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="bg-green-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-green-600"></i>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Telefone</p>
                    <p className="text-slate-600">(17) 99734‑6470</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-blue-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-blue-600"></i>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">E-mail</p>
                    <p className="text-slate-600">peritojpss@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-purple-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-purple-600"></i>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Endereço</p>
                    <p className="text-slate-600">Alameda Sabará, 54 — Bebedouro/SP</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-slate-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-linkedin-fill text-slate-600"></i>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">LinkedIn</p>
                    <a
                      href="https://www.linkedin.com/in/joaopauloscardelatoseverino"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 cursor-pointer"
                    >
                      Perfil Profissional
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-orange-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-global-line text-orange-600"></i>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Website</p>
                    <p className="text-slate-600">www.peritojpss.com.br</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-slate-900 mb-6">
                Solicitar Orçamento
              </h3>
              
              {submitMessage && (
                <div className={`p-4 rounded-lg mb-6 ${
                  submitMessage.includes('sucesso') 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit} id="contact-form" data-readdy-form>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-slate-700 mb-1">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                      placeholder="seu@email.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-medium text-slate-700 mb-1">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="assunto" className="block text-sm font-medium text-slate-700 mb-1">
                      Tipo de Perícia *
                    </label>
                    <select
                      id="assunto"
                      name="assunto"
                      value={formData.assunto}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Selecione o tipo de perícia</option>
                      <option value="insalubridade">Insalubridade</option>
                      <option value="periculosidade">Periculosidade</option>
                      <option value="ergonomia">Ergonomia e Saúde Ocupacional</option>
                      <option value="energia">Consumo de Energia</option>
                      <option value="maquinas">Máquinas e Equipamentos</option>
                      <option value="veiculos">Veículos Automotores</option>
                      <option value="climatizacao">Sistemas de Climatização</option>
                      <option value="assistencia">Assistência Técnica</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="mensagem" className="block text-sm font-medium text-slate-700 mb-1">
                      Mensagem *
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm resize-none"
                      placeholder="Descreva brevemente seu caso e informe o número do processo se houver..."
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.mensagem.length}/500 caracteres
                    </p>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full"
                    size="lg"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
